import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  CalendarDays,
  Download,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  FlaskConical,
  Atom,
  Dna,
  Globe,
  Zap,
  Droplets,
  Microscope,
  Leaf,
  List,
  Sparkles,
  PenTool,
  Clock,
  FileQuestion,
  Files
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { juniorScienceCurriculum } from '@/data/juniorScienceCurriculum';
import { juniorScienceWeeklyLessons } from '@/data/juniorScienceWeeklyLessons';
import { 
  year7ScienceUnitTests,
  year8ScienceUnitTests,
  year9ScienceUnitTests,
  year10ScienceUnitTests
} from '@/data/juniorScienceUnitTests';
import { 
  year7ScienceStudentNotes,
  year8ScienceStudentNotes,
  year9ScienceStudentNotes,
  year10ScienceStudentNotes
} from '@/data/juniorScienceStudentNotes';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function JuniorScienceCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 7");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        const years = [...new Set(classes
          .filter(cls => 
            cls.subject?.toLowerCase().includes('science') ||
            cls.subject?.toLowerCase().includes('basic science')
          )
          .map(cls => cls.yearLevel)
        )].sort();
        
        if (years.length > 0) {
          setTeacherYears(years);
          const yearNum = parseInt(years[0].replace(/\D/g, ''));
          if (yearNum >= 7 && yearNum <= 10) {
            setSelectedYear(`Year ${yearNum}`);
          }
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  const curriculum = juniorScienceCurriculum;
  const yearKey = selectedYear.toLowerCase().replace(' ', '');
  const currentYearData = curriculum.years[yearKey];
  
  const getStudentNotes = () => {
    switch(selectedYear) {
      case "Year 7": return year7ScienceStudentNotes;
      case "Year 8": return year8ScienceStudentNotes;
      case "Year 9": return year9ScienceStudentNotes;
      case "Year 10": return year10ScienceStudentNotes;
      default: return year7ScienceStudentNotes;
    }
  };

  const getUnitTests = () => {
    switch(selectedYear) {
      case "Year 7": return year7ScienceUnitTests;
      case "Year 8": return year8ScienceUnitTests;
      case "Year 9": return year9ScienceUnitTests;
      case "Year 10": return year10ScienceUnitTests;
      default: return year7ScienceUnitTests;
    }
  };

  const themes = curriculum.themes;
  const studentNotes = getStudentNotes();
  const unitTests = getUnitTests();

  const getThemeIcon = (code) => {
    switch(code) {
      case 'A': return <Zap className="h-5 w-5 text-yellow-600" />;
      case 'B': return <FlaskConical className="h-5 w-5 text-blue-600" />;
      case 'C': return <Dna className="h-5 w-5 text-green-600" />;
      case 'D': return <Globe className="h-5 w-5 text-orange-600" />;
      default: return <Microscope className="h-5 w-5 text-purple-600" />;
    }
  };

  const getThemeColor = (code) => {
    switch(code) {
      case 'A': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'B': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'C': return 'bg-green-100 text-green-800 border-green-300';
      case 'D': return 'bg-orange-100 text-orange-800 border-orange-300';
      default: return 'bg-purple-100 text-purple-800 border-purple-300';
    }
  };

  const handlePrint = () => {
    window.print();
    toast.success('Curriculum sent to printer');
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <Card className="bg-gradient-to-r from-teal-600 to-cyan-700 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Microscope className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl">Junior Basic Science Curriculum</CardTitle>
                <CardDescription className="text-teal-100">
                  Years 7-10 • Vanuatu National Curriculum
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[120px] bg-white/20 border-white/30 text-white">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Year 7">Year 7</SelectItem>
                  <SelectItem value="Year 8">Year 8</SelectItem>
                  <SelectItem value="Year 9">Year 9</SelectItem>
                  <SelectItem value="Year 10">Year 10</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="secondary" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100 p-1 flex-wrap">
          <TabsTrigger value="curriculum" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <BookOpen className="h-4 w-4 mr-2" />
            Curriculum
          </TabsTrigger>
          <TabsTrigger value="schemes" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <CalendarDays className="h-4 w-4 mr-2" />
            Scheme
          </TabsTrigger>
          <TabsTrigger value="term-exam" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white bg-rose-100">
            <FileQuestion className="h-4 w-4 mr-2" />
            Term Exam
          </TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-2" />
            Study Notes
          </TabsTrigger>
          <TabsTrigger value="tests" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Unit Tests
          </TabsTrigger>
          <TabsTrigger value="lesson-plans" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <PenTool className="h-4 w-4 mr-2" />
            Lesson Plans
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white bg-blue-100">
            <Files className="h-4 w-4 mr-2" />
            Quick Print
          </TabsTrigger>
          <TabsTrigger value="ai-planner" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Sparkles className="h-4 w-4 mr-2" />
            AI Lesson Plans
          </TabsTrigger>
        </TabsList>

        {/* Schemes Tab - Weekly Scheme of Work */}
        <TabsContent value="schemes" className="space-y-6">
          <SubjectSchemeOfWork
            subject="Junior Science"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={juniorScienceWeeklyLessons}
            subjectColor="teal"
          />
        </TabsContent>

        {/* Term Exam Tab */}
        <TabsContent value="term-exam" className="space-y-6">
          <TermExamGenerator
            subject="Junior Science"
            year={parseInt(selectedYear.replace('Year ', ''))}
            unitTests={selectedYear === 'Year 7' ? year7ScienceUnitTests : selectedYear === 'Year 8' ? year8ScienceUnitTests : selectedYear === 'Year 9' ? year9ScienceUnitTests : year10ScienceUnitTests}
            subjectColor="teal"
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" className="space-y-6">
          <QuickPrintAll
            subject="Junior Science"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={juniorScienceWeeklyLessons}
            subjectColor="teal"
          />
        </TabsContent>

        {/* Lesson Plans Tab */}
        <TabsContent value="lesson-plans" className="space-y-6">
          <LessonPlanGenerator
            subject="Junior Science"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={juniorScienceWeeklyLessons}
            subjectColor="teal"
          />
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum" className="space-y-6">
          {/* Themes Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-teal-600" />
                Science Themes
              </CardTitle>
              <CardDescription>Four major themes covering all aspects of junior science</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {themes.map((theme) => (
                  <Card key={theme.code} className={`border-2 ${getThemeColor(theme.code)}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        {getThemeIcon(theme.code)}
                        <span className="font-bold">Theme {theme.code}</span>
                      </div>
                      <h4 className="font-semibold text-sm mb-1">{theme.name}</h4>
                      <p className="text-xs opacity-80">{theme.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Year Overview */}
          {currentYearData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-teal-600" />
                  {selectedYear} Overview
                </CardTitle>
                <CardDescription>
                  {currentYearData.hours} teaching hours • {currentYearData.focus}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <Badge variant="outline" className="text-teal-600 border-teal-600">
                    <Clock className="h-3 w-3 mr-1" />
                    {currentYearData.hours} hours total
                  </Badge>
                  <Badge variant="outline" className="text-teal-600 border-teal-600">
                    <List className="h-3 w-3 mr-1" />
                    {currentYearData.strands ? `${currentYearData.strands.length} strands` : `${currentYearData.units?.length || 0} units`}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Curriculum Content */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-teal-600" />
                {selectedYear} {currentYearData?.strands ? 'Strands & Learning Outcomes' : 'Units'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentYearData?.strands ? (
                <Accordion type="single" collapsible className="space-y-2">
                  {currentYearData.strands.map((strand, sIdx) => (
                    <AccordionItem key={strand.id} value={`strand-${sIdx}`} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <Badge className={sIdx === 0 ? 'bg-blue-600' : 'bg-green-600'}>
                            Strand {sIdx + 1}
                          </Badge>
                          <span className="font-semibold text-left">{strand.title}</span>
                          <Badge variant="secondary" className="ml-2">
                            {strand.subStrands.reduce((sum, s) => sum + s.slos.length, 0)} SLOs
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4 space-y-4">
                        <p className="text-sm text-gray-600 mb-3">{strand.description}</p>
                        {strand.majorLearningOutcome && (
                          <div className="bg-gray-50 p-3 rounded-lg mb-3">
                            <h4 className="font-semibold text-sm text-gray-700 mb-1">Major Learning Outcome:</h4>
                            <p className="text-sm text-gray-600">{strand.majorLearningOutcome}</p>
                          </div>
                        )}
                        {strand.subStrands.map((sub, subIdx) => (
                          <div key={sub.id} className="border-l-4 border-teal-400 pl-4 py-2">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-teal-700 border-teal-400">{sub.id}</Badge>
                              <span className="font-semibold text-sm">{sub.title}</span>
                              <Badge variant="secondary" className="text-xs">{sub.slos.length} SLOs</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mb-2">{sub.description}</p>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                              {sub.slos.map((slo, i) => (
                                <li key={i}>{slo}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
              <Accordion type="single" collapsible className="space-y-2">
                {currentYearData?.units?.map((unit, index) => (
                  <AccordionItem key={unit.id} value={`unit-${index}`} className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={getThemeColor(unit.theme)}>
                          {unit.theme}
                        </Badge>
                        <span className="font-semibold">{unit.id}: {unit.name}</span>
                        <Badge variant="secondary" className="ml-2">
                          {unit.hours} hrs
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Learning Objectives:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {unit.objectives?.map((obj, i) => (
                            <li key={i}>{obj}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Key Content:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {unit.content?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Suggested Activities:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {unit.activities?.map((activity, i) => (
                            <li key={i}>{activity}</li>
                          ))}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              )}
            </CardContent>
          </Card>

          {/* Assessment Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-teal-600" />
                Assessment Structure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-2 border-teal-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="h-4 w-4 text-teal-600" />
                      Practical Skills (30%)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{curriculum.assessment.practical.description}</p>
                    <div className="mt-2">
                      <Progress value={30} className="h-2" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-cyan-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PenTool className="h-4 w-4 text-cyan-600" />
                      Written Examination (70%)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Paper 1 (Multiple Choice)</span>
                        <Badge variant="outline">40 marks</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Paper 2 (Structured)</span>
                        <Badge variant="outline">60 marks</Badge>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Progress value={70} className="h-2" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Study Notes Tab */}
        <TabsContent value="notes" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-teal-600" />
                {selectedYear} Study Notes
              </CardTitle>
              <CardDescription>Comprehensive notes for all topics</CardDescription>
            </CardHeader>
            <CardContent>
              {studentNotes?.strands && Object.entries(studentNotes.strands).map(([strandKey, strand]) => (
                <div key={strandKey} className="mb-6">
                  <h3 className="text-lg font-bold text-teal-700 mb-4">{strand.name}</h3>
                  <Accordion type="single" collapsible className="space-y-2">
                    {strand.units?.map((unit, idx) => (
                      <AccordionItem key={idx} value={`notes-${strandKey}-${idx}`} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <span className="font-semibold">{unit.name}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4 space-y-4">
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Objectives:</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                              {unit.objectives?.map((obj, i) => (
                                <li key={i}>{obj}</li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Key Terms:</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {unit.keyTerms?.map((term, i) => (
                                <div key={i} className="bg-gray-50 p-2 rounded">
                                  <span className="font-medium text-teal-700">{term.term}:</span>
                                  <span className="text-sm text-gray-600 ml-1">{term.definition}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Study Notes:</h4>
                            <div className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg">
                              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                                {unit.studyNotes}
                              </pre>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Practice Questions:</h4>
                            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                              {unit.practiceQuestions?.map((q, i) => (
                                <li key={i}>{q}</li>
                              ))}
                            </ol>
                          </div>
                          
                          {unit.examTips && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <h4 className="font-semibold text-sm text-yellow-800 mb-1">💡 Exam Tips:</h4>
                              <p className="text-sm text-yellow-700">{unit.examTips}</p>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Tests Tab */}
        <TabsContent value="tests" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-teal-600" />
                {selectedYear} Unit Tests
              </CardTitle>
              <CardDescription>Assessment questions and answers</CardDescription>
            </CardHeader>
            <CardContent>
              {unitTests?.strands && Object.entries(unitTests.strands).map(([strandKey, strand]) => (
                <div key={strandKey} className="mb-6">
                  <h3 className="text-lg font-bold text-teal-700 mb-4">{strand.name}</h3>
                  <Accordion type="single" collapsible className="space-y-2">
                    {strand.tests?.map((test, idx) => (
                      <AccordionItem key={idx} value={`test-${strandKey}-${idx}`} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{test.title}</span>
                            <Badge variant="outline">{test.duration}</Badge>
                            <Badge variant="secondary">{test.totalMarks} marks</Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4">
                          <div className="space-y-4">
                            {test.questions?.map((q, qIdx) => (
                              <Card key={qIdx} className="border">
                                <CardContent className="pt-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="font-semibold text-teal-700">Question {q.id}</span>
                                    <Badge>{q.marks} marks</Badge>
                                  </div>
                                  <div className="mb-3 whitespace-pre-line text-gray-700">{q.question}</div>
                                  <Accordion type="single" collapsible>
                                    <AccordionItem value="answer" className="border-0">
                                      <AccordionTrigger className="text-sm text-teal-600 py-2">
                                        Show Answer
                                      </AccordionTrigger>
                                      <AccordionContent>
                                        <div className="bg-green-50 p-3 rounded whitespace-pre-line text-sm text-gray-700">
                                          {q.answer}
                                        </div>
                                      </AccordionContent>
                                    </AccordionItem>
                                  </Accordion>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Lesson Plans Tab */}
        <TabsContent value="ai-planner">
          <AILessonPlanGenerator 
            subject="Basic Science"
            year={selectedYear}
            curriculumData={currentYearData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
